import type { EventObject } from '../eventTypes';
import type { TaskObject } from '../../tasks/types';
import { isTaskArchived } from '../../tasks/helpers';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Format a Date's LOCAL calendar day as YYYY-MM-DD (no timezone shift). */
function localDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Map a Kanban task to a synthetic all-day/timed calendar event, or null when
 * the task should not appear (no due date, archived, or already done).
 *
 * Today the Tasks UI stores `dueAt` as a noon-local sentinel (localDateToISO),
 * so every task is all-day; this helper is timed-capable for when a real
 * time-of-day is ever stored.
 */
export function taskToCalendarEvent(task: TaskObject): EventObject | null {
  if (!task.dueAt || task.status === 'done' || isTaskArchived(task)) return null;

  const etag = task._etag ?? (task.version != null ? String(task.version) : undefined);
  const nowIso = new Date().toISOString();
  const common = {
    id: `task-event-${task.id}`,
    type: 'event' as const,
    title: task.title || task.what || 'Task',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    eventType: 'activity' as const,
    createdAt: task.createdAt ?? nowIso,
    updatedAt: nowIso,
    version: 0,
    _isVirtual: true,
    _sourceTaskId: task.id,
    _sourceTaskEtag: etag,
    metadata: { customColor: '#7c3aed' },
  };

  if (DATE_ONLY_RE.test(task.dueAt)) {
    return { ...common, allDay: true, startAt: task.dueAt, endAt: task.dueAt };
  }

  const due = new Date(task.dueAt);
  if (Number.isNaN(due.getTime())) return null;

  const isNoonSentinel =
    due.getHours() === 12 && due.getMinutes() === 0 && due.getSeconds() === 0 && due.getMilliseconds() === 0;
  if (isNoonSentinel) {
    const d = localDateString(due);
    return { ...common, allDay: true, startAt: d, endAt: d };
  }

  // A task due time is a single specific moment, not a range — represent it as a
  // zero-duration instant (startAt === endAt). The Day/Week views render it as a
  // single-time marker (clamped to a minimum height) rather than a span.
  const iso = due.toISOString();
  return { ...common, allDay: false, startAt: iso, endAt: iso };
}
